import React from 'react';
import LoadingSpinner from '../src/components/LoadingSpinner';
import {
  Users,
  CheckSquare,
  ArrowRight,
  TrendingUp,
  Calendar,
  Zap,
  Clock,
  ExternalLink,
  FileText,
  Plus,
  BadgeCheck
} from 'lucide-react';
import { ehVendaEfetiva } from '../src/lib/dashboard/vendasEfetivas';
import { criarChecagemTarefaArquivada } from '../src/lib/tarefaArquivada';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../src/services/api';
import { supabase } from '../src/lib/supabase';
import NegotiationWizard from './CRM/components/NewNegotiationWizard/NegotiationWizard';
import NegotiationDetailModal from './CRM/components/NegotiationDetails/NegotiationDetailModal';
import TaskModal from './Tarefas/TaskModal';
import DashboardMap from './DashboardMap';

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const [propostas, setPropostas] = React.useState<any[]>([]);
  const [tarefas, setTarefas] = React.useState<any[]>([]);
  const [user, setUser] = React.useState<any>(null);
  const [profiles, setProfiles] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  const [showNegotiationWizard, setShowNegotiationWizard] = React.useState(false);
  const [showTaskModal, setShowTaskModal] = React.useState(false);
  const [createdNegociacaoId, setCreatedNegociacaoId] = React.useState<number | null>(null);

  // Date filter — default to current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [dateStart, setDateStart] = React.useState(firstDay.toISOString().split('T')[0]);
  const [dateEnd, setDateEnd] = React.useState(lastDay.toISOString().split('T')[0]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [negociacoesRes, tarefasRes, userData, profilesRes] = await Promise.all([
          api.propostas.list(),
          api.tarefas.list(),
          api.auth.getUser(),
          api.auth.listProfiles() // Fetch profiles to check roles
        ]);

        // To make sure we get all negociacoes regardless of stage.
        // Supabase corta em 1000 linhas por resposta — paginamos até esgotar.
        const PAGE = 1000;
        const allNegociacoes: any[] = [];
        for (let page = 0; ; page++) {
          const from = page * PAGE;
          const { data } = await supabase
            .from('negociacoes')
            .select('*')
            .order('id', { ascending: true })
            .range(from, from + PAGE - 1);
          const batch = data || [];
          allNegociacoes.push(...batch);
          if (batch.length < PAGE) break;
        }

        setPropostas(allNegociacoes);
        setTarefas(tarefasRes);
        setUser(userData);
        setProfiles(profilesRes);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [showNegotiationWizard, showTaskModal]); // refetch when modals close to update stats

  if (loading) return <LoadingSpinner label="Carregando dashboard..." />;

  const startDate = new Date(dateStart + 'T00:00:00');
  const endDate = new Date(dateEnd + 'T23:59:59');

  // Implement Role-based filtering
  const userRole = user?.role?.toUpperCase() || '';
  const userId = user?.id;

  const getRoleById = (id: string) => {
    const p = profiles.find(profile => profile.id === id);
    return (p?.role || '').toUpperCase();
  };

  const roleFilteredPropostas = propostas.filter(p => {
    if (userRole === 'VENDEDOR' || userRole === 'VENDEDOR_PARCEIRO') {
      return p.responsavel_id === userId;
    }
    if (userRole === 'PARCEIRO') {
      const pProfile = profiles.find(pr => pr.id === p.responsavel_id);
      return p.responsavel_id === userId || pProfile?.parceiro_id === userId;
    }
    if (userRole === 'GERENTE_PARCERIAS') {
      const responsavelRole = getRoleById(p.responsavel_id);
      return responsavelRole === 'PARCEIRO' || responsavelRole === 'VENDEDOR_PARCEIRO' || p.responsavel_id === userId;
    }
    // ADMIN, ANALISTA, GERENTE COMERCIAL see all
    return true;
  });

  // Tarefa de quem saiu da empresa (inativo ou excluído) fica fora do card e da lista
  // de recentes, a mesma regra da tela de Tarefas — ver src/lib/tarefaArquivada.ts.
  const tarefaArquivada = criarChecagemTarefaArquivada(profiles);

  const roleFilteredTarefas = tarefas.filter(t => {
    if (tarefaArquivada(t)) return false;
    if (userRole === 'VENDEDOR' || userRole === 'VENDEDOR_PARCEIRO') {
      return t.responsavel_id === userId;
    }
    if (userRole === 'PARCEIRO') {
      const responsavel = profiles.find(pr => pr.id === t.responsavel_id);
      return t.responsavel_id === userId || responsavel?.parceiro_id === userId;
    }
    if (userRole === 'GERENTE_PARCERIAS') {
      const responsavelRole = getRoleById(t.responsavel_id);
      return responsavelRole === 'PARCEIRO' || responsavelRole === 'VENDEDOR_PARCEIRO' || t.responsavel_id === userId;
    }
    // ADMIN, ANALISTA, GERENTE COMERCIAL see all
    return true;
  });


  // Filtering Logic
  const activeProposals = roleFilteredPropostas.filter(p => {
    if (['termo_assinado', 'termo_reprovado', 'perdido', 'em_pausa'].includes(p.status)) return false;
    const d = new Date(p.created_at || p.updated_at);
    return d >= startDate && d <= endDate;
  });

  const dentroDoPeriodo = (iso: string | null) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d >= startDate && d <= endDate;
  };

  // Negócios Fechados = termos ASSINADOS no período, a mesma conta do "Termos Assinados" do
  // Dashboard de Negociações do CRM. Antes contava só quem ainda estava em `termo_assinado`
  // (esperando análise): o termo saía do card ao ser aprovado ou reprovado — em out/2026 o
  // card mostrava 6 de 30 assinados.
  const closedProposals = roleFilteredPropostas.filter(p => dentroDoPeriodo(p.data_assinatura_termo));

  // Vendas Efetivas (#6421): das assinadas no período, as que já foram aprovadas.
  const vendasEfetivas = roleFilteredPropostas.filter(p => ehVendaEfetiva(p, dentroDoPeriodo));

  const pendingTasks = roleFilteredTarefas.filter(t => {
    return !['concluido', 'cancelado'].includes(t.status);
  });

  // Recent tasks: not canceled, max 5, order by updated_at desc
  const recentTasks = roleFilteredTarefas
    .filter(t => t.status !== 'cancelado')
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-8 w-full mx-auto animate-fade-in">
      {/* Header and Filter */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-poppins font-bold text-gray-900 dark:text-white">
            Bem-vindo, {user?.full_name?.split(' ')[0] || 'Gestor'} 👋
          </h1>
          <p className="text-gray-500 dark:text-gray-400">Confira o que está acontecendo nas operações da Vigor no período selecionado.</p>
        </div>

        {/* Date Filter */}
        <div className="flex w-full md:w-auto">
          <div className="w-full flex-col sm:flex-row flex sm:items-center gap-3 bg-white dark:bg-gray-800 px-4 py-3 sm:py-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm md:w-auto">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Período:</span>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                <input
                  type="date"
                  value={dateStart}
                  onChange={e => setDateStart(e.target.value)}
                  className="w-full sm:w-auto text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1.5 sm:py-1 outline-none focus:border-primary"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="date"
                  value={dateEnd}
                  onChange={e => setDateEnd(e.target.value)}
                  className="w-full sm:w-auto text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded px-2 py-1.5 sm:py-1 outline-none focus:border-primary"
                />
              </div>
            </div>
            <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1"></div>
            <button
              onClick={() => {
                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
                const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                setDateStart(firstDay.toISOString().split('T')[0]);
                setDateEnd(lastDay.toISOString().split('T')[0]);
              }}
              className="w-full justify-center sm:w-auto flex items-center gap-2 px-3 py-2 sm:py-1 bg-primary/10 dark:bg-primary/20 text-primary text-sm font-semibold rounded-lg hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors mt-2 sm:mt-0"
            >
              <Calendar size={14} />
              Mês Atual
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
        <h2 className="text-lg font-poppins font-bold text-gray-900 dark:text-white mb-6">Ações Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowNegotiationWizard(true)}
            className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-100 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all text-center group/btn">
            <Plus className="text-blue-500 mb-2" />
            <span className="text-sm font-semibold text-gray-700 dark:text-white group-hover/btn:text-blue-600 transition-colors">Criar nova negociação</span>
          </button>
          <button
            onClick={() => setShowTaskModal(true)}
            className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-100 dark:border-gray-600 hover:border-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all text-center group/btn">
            <CheckSquare className="text-amber-500 mb-2" />
            <span className="text-sm font-semibold text-gray-700 dark:text-white group-hover/btn:text-amber-600 transition-colors">Criar nova tarefa</span>
          </button>
          <button
            onClick={() => navigate('/tarefas?view=calendar')}
            className="flex flex-col items-center justify-center p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl border border-gray-100 dark:border-gray-600 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all text-center group/btn">
            <Calendar className="text-purple-500 mb-2" />
            <span className="text-sm font-semibold text-gray-700 dark:text-white group-hover/btn:text-purple-600 transition-colors">Ver agenda</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1 - Negociações Ativas */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col gap-4 group hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
              <TrendingUp size={24} />
            </div>
          </div>
          <div>
            <h3 className="text-gray-500 dark:text-gray-400 font-medium">Negociações Ativas</h3>
            <p className="text-4xl font-montserrat font-bold text-gray-900 dark:text-white mt-1">{activeProposals.length}</p>
            <p className="text-sm text-gray-400 mt-2">No período selecionado.</p>
          </div>
          <Link
            to="/crm"
            className="mt-4 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white font-semibold py-2 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-all"
          >
            Acessar CRM <ArrowRight size={18} />
          </Link>
        </div>

        {/* Card 2 - Negócios Fechados */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col gap-4 group hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-xl text-green-600 dark:text-green-400">
              <CheckSquare size={24} />
            </div>
          </div>
          <div>
            <h3 className="text-gray-500 dark:text-gray-400 font-medium">Negócios Fechados</h3>
            <p className="text-4xl font-montserrat font-bold text-gray-900 dark:text-white mt-1">{closedProposals.length}</p>
            <p className="text-sm text-gray-400 mt-2">Termos assinados no período.</p>
          </div>
          <Link
            to="/crm"
            className="mt-4 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white font-semibold py-2 rounded-xl group-hover:bg-green-600 group-hover:text-white transition-all"
          >
            Acessar CRM <ArrowRight size={18} />
          </Link>
        </div>

        {/* Card 3 - Vendas Efetivas (#6421) */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col gap-4 group hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl text-emerald-600 dark:text-emerald-400">
              <BadgeCheck size={24} />
            </div>
          </div>
          <div>
            <h3 className="text-gray-500 dark:text-gray-400 font-medium">Vendas Efetivas</h3>
            <p className="text-4xl font-montserrat font-bold text-gray-900 dark:text-white mt-1">{vendasEfetivas.length}</p>
            <p className="text-sm text-gray-400 mt-2">
              Aprovadas, das {closedProposals.length} assinadas no período.
            </p>
          </div>
          <Link
            to="/crm"
            className="mt-4 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white font-semibold py-2 rounded-xl group-hover:bg-emerald-600 group-hover:text-white transition-all"
          >
            Acessar CRM <ArrowRight size={18} />
          </Link>
        </div>

        {/* Card 4 - Tarefas Pendentes */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col gap-4 group hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
              <Clock size={24} />
            </div>
          </div>
          <div>
            <h3 className="text-gray-500 dark:text-gray-400 font-medium">Tarefas Pendentes</h3>
            <p className="text-4xl font-montserrat font-bold text-gray-900 dark:text-white mt-1">{pendingTasks.length}</p>
            <p className="text-sm text-gray-400 mt-2">Até o momento.</p>
          </div>
          <Link
            to="/tarefas"
            className="mt-4 flex items-center justify-center gap-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-white font-semibold py-2 rounded-xl group-hover:bg-amber-500 group-hover:text-white transition-all"
          >
            Acessar Tarefas <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Tasks Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-poppins font-bold text-gray-900 dark:text-white">Tarefas Recentes</h2>
            <Link to="/tarefas" className="text-sm text-primary font-semibold hover:underline">Ver todas</Link>
          </div>
          <div className="space-y-4">
            {recentTasks.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma tarefa recente.</p>
            ) : (
              recentTasks.map(tarefa => {
                const isConcluido = tarefa.status === 'concluido';
                const dateToShow = isConcluido ? tarefa.updated_at : tarefa.vencimento;
                const dateLabel = isConcluido ? 'Concluída em' : 'Vencimento';

                return (
                  <div key={tarefa.id} className="flex gap-4 items-start p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors">
                    <div className={`shrink-0 w-10 h-10 ${isConcluido ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400' : 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'} rounded-full flex items-center justify-center`}>
                      {isConcluido ? <CheckSquare size={18} /> : <FileText size={18} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2">{tarefa.titulo}</p>
                      {tarefa.descricao && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{tarefa.descricao}</p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 capitalize">
                        {dateLabel}: {dateToShow ? new Date(dateToShow).toLocaleDateString('pt-BR') : 'Sem data'}
                      </p>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        <DashboardMap />
      </div>



      <NegotiationWizard
        isOpen={showNegotiationWizard}
        onClose={() => setShowNegotiationWizard(false)}
        onSuccess={(negociacaoId) => {
          setShowNegotiationWizard(false);
          setCreatedNegociacaoId(negociacaoId);
        }}
      />
      <NegotiationDetailModal
        isOpen={createdNegociacaoId !== null}
        onClose={() => setCreatedNegociacaoId(null)}
        negotiationId={createdNegociacaoId}
        onUpdate={() => {}}
      />
      <TaskModal isOpen={showTaskModal} onClose={() => setShowTaskModal(false)} onTaskCreated={() => {}} />
    </div>
  );
};

export default DashboardHome;
